import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from './asc-progress-circle.styles';

export type ProgressCircleSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
export type ProgressCircleStatus = 'empty' | 'in-progress' | 'complete';

/**
 * A circular progress indicator for showing completion status, multi-step
 * workflows, and upload progress.
 *
 * The fill arc is driven by the `percent` property — a number (0–100)
 * that is displayed as `"N%"` in the ring center and used directly to compute
 * the arc fill, reflected `status` attribute, and `aria-valuenow`. An optional
 * `sublabel` property provides a short descriptive label whose position varies
 * with `size`: below the ring for `xxs`, inside the ring above the value for
 * `xs`–`lg`.
 *
 * @element asc-progress-circle
 *
 * @csspart base           - Outer flex-column wrapper.
 * @csspart ring-wrapper   - Fixed-size circle container; establishes positioning context.
 * @csspart svg            - The SVG element containing track and fill circles.
 * @csspart track          - Background ring circle.
 * @csspart fill           - Progress arc circle; stroke-dashoffset driven by percent value.
 * @csspart inner-content  - Overlay div centered inside the ring; rendered for all sizes.
 * @csspart sublabel-inner - Sublabel text rendered inside the ring above the value for non-`xxs` sizes. Hidden via `hidden` when `sublabel` is empty.
 * @csspart value          - Percentage label wrapper; always inside the ring.
 * @csspart sublabel-below - Sublabel text rendered below the ring for `xxs` size. Hidden via `hidden` when `sublabel` is empty.
 *
 * @cssprop [--asc-progress-circle-size]                   - Container diameter (width & height of ring-wrapper). Set per size.
 * @cssprop [--asc-progress-circle-track-color]            - Track ring stroke color. Default: `var(--asc-bg-secondary)`.
 * @cssprop [--asc-progress-circle-fill-color]             - Fill arc stroke color for the in-progress state. Default: `var(--asc-fg-brand-primary)`.
 * @cssprop [--asc-progress-circle-fill-color-empty]       - Fill stroke color when status is `empty`. Defaults to fill-color.
 * @cssprop [--asc-progress-circle-fill-color-complete]    - Fill stroke color when status is `complete`. Defaults to fill-color.
 * @cssprop [--asc-progress-circle-transition-duration]    - Animation duration for stroke-dashoffset. Default: `300ms`.
 * @cssprop [--asc-progress-circle-value-color]            - Value label text color. Default: `var(--asc-text-primary)`.
 * @cssprop [--asc-progress-circle-value-font-family]      - Value label font family. Body for `xxs`; display for `xs`–`lg`.
 * @cssprop [--asc-progress-circle-value-font-size]        - Value label font size. Set per size.
 * @cssprop [--asc-progress-circle-value-font-weight]      - Value label font weight. Default: `var(--asc-font-weight-semibold)`.
 * @cssprop [--asc-progress-circle-value-line-height]      - Value label line height. Set per size.
 * @cssprop [--asc-progress-circle-value-letter-spacing]   - Value label letter spacing. `−2px` for `md`/`lg`; `0` otherwise.
 * @cssprop [--asc-progress-circle-sublabel-color]         - Sublabel text color. Default: `var(--asc-text-tertiary)`.
 * @cssprop [--asc-progress-circle-sublabel-font-family]   - Sublabel font family. Default: `var(--asc-font-family-body)`.
 * @cssprop [--asc-progress-circle-sublabel-font-size]     - Sublabel font size. `text-xs` for `xxs`/`xs`/`sm`; `text-sm` for `md`/`lg`.
 * @cssprop [--asc-progress-circle-sublabel-font-weight]   - Sublabel font weight. Default: `var(--asc-font-weight-medium)`.
 * @cssprop [--asc-progress-circle-sublabel-line-height]   - Sublabel line height. Set per size.
 * @cssprop [--asc-progress-circle-gap]                    - Gap between ring and below-sublabel (`xxs` only). Default: `var(--asc-spacing-xs)`.
 * @cssprop [--asc-progress-circle-content-padding]        - Inline padding inside inner-content to prevent text overflow.
 */
@customElement('asc-progress-circle')
export class AscProgressCircle extends LitElement {
  static override styles = styles;

  /** Size of the circle. Controls container diameter, typography scale, and sublabel position. Default: `'md'`. */
  @property({ type: String, reflect: true }) size: ProgressCircleSize = 'md';

  /**
   * Numeric progress value between 0 and 100. Values outside this range are clamped.
   * Displayed as `"N%"` in the ring center.
   */
  @property({ type: Number, attribute: 'percent', reflect: true }) percent = 0;

  /** Optional short descriptive label rendered inside or below the ring depending on `size`. */
  @property({ type: String, reflect: true }) sublabel = '';

  private static readonly _CIRCUMFERENCE = 2 * Math.PI * 45; // ≈ 282.74

  private get _clamped(): number {
    return Math.min(100, Math.max(0, this.percent));
  }

  private get _statusValue(): ProgressCircleStatus {
    return this._clamped === 0 ? 'empty' : this._clamped === 100 ? 'complete' : 'in-progress';
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', '100');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Progress');
    }
  }

  override updated(): void {
    this.setAttribute('aria-valuenow', String(this._clamped));
    this.setAttribute('status', this._statusValue);
  }

  override render() {
    const c = AscProgressCircle._CIRCUMFERENCE;
    // At 0%: keep a tiny non-zero offset to preserve the round-cap dot
    const offset = this._clamped === 0 ? c - 0.01 : c * (1 - this._clamped / 100);

    return html`
      <div part="base">
        <div part="ring-wrapper">
          <svg part="svg" viewBox="0 0 100 100" aria-hidden="true">
            <circle
              part="track"
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke-width="10"
              stroke-linecap="round"
            ></circle>
            <circle
              part="fill"
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke-width="10"
              stroke-linecap="round"
              stroke-dasharray="${c}"
              stroke-dashoffset="${offset}"
              transform="rotate(-90 50 50)"
            ></circle>
          </svg>
          <div part="inner-content">
            ${this.size !== 'xxs'
              ? html`<span part="sublabel-inner" ?hidden=${!this.sublabel}>${this.sublabel}</span>`
              : ''}
            <span part="value" aria-hidden="true">${this._clamped}%</span>
          </div>
        </div>
        ${this.size === 'xxs'
          ? html`<span part="sublabel-below" ?hidden=${!this.sublabel}>${this.sublabel}</span>`
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-progress-circle': AscProgressCircle;
  }
}
